import { useState } from 'react';

import { abortAIAgentTaskPath } from '@/client/BFF/Workspaces/Projects/AIAgentTasks/aiAgentTasksPath';
import { AIAgentTask } from '@/client/BFF/Workspaces/Projects/AIAgentTasks/types';
import useAIAgentTaskShow from '@/client/BFF/Workspaces/Projects/AIAgentTasks/useAIAgentTaskShow';
import useTaskLog from '@/client/BFF/Workspaces/Projects/AIAgentTasks/useTaskLog';
import { AIHistoryItem, AILoading, AIMessage } from '@/components/AIChat/AIChat';
import AIChatLogDialog, { type AIChatLogDialogTurnInfo } from '@/components/AIChat/AIChatLogDialog';
import AIDrawer from '@/components/AIDrawer/AIDrawer';
import { HEADER_HEIGHT } from '@/constants';
import { segmentTrack } from '@/hooks/analytics/useTracking';
import useMonolithApiMutation from '@/hooks/api/useMonolithApiMutation';
import { AIRunType, Build } from '@/models/Build';
import { SimpleErrorResponse } from '@/models/Error';
import { abortBuildPath } from '@/utils/monolithApiRouteService';

import useBuildAIConversationHistory from '../AIChat/hooks/useBuildAIConversationHistory';
import useLog from '../AIChat/hooks/useLog';
import { useOverlaySidebar } from '../page/PlatformShell/OverlaySidebar';

type MultiTurnProps = {
  buildSlug: string;
  lastTurnId: string;
  appSlug: string;
  isTaskPath?: boolean;
  onClose: () => void;
  onUpdate: (props: Partial<MultiTurnProps>) => void;
  onHide: () => void;
};

export const useMultiTurnFixer = () => {
  const { show, state } = useOverlaySidebar();
  const isMultiTurn = state.variant === 'multi-turn';
  return {
    show,
    lastTurnId: isMultiTurn ? state.props.lastTurnId : undefined,
    isTaskPath: isMultiTurn ? state.props.isTaskPath : undefined,
  };
};

const MultiTurnFixer = (props: MultiTurnProps) => {
  const { onClose, onHide, onUpdate, buildSlug, appSlug, lastTurnId, isTaskPath } = props;

  const [viewedTurn, setViewedTurn] = useState<AIChatLogDialogTurnInfo>();
  const [message, setMessage] = useState('');

  const { history, inProgress, refetch } = useBuildAIConversationHistory({ buildSlug });

  const buildLog = useLog({ appSlug, buildSlug: isTaskPath ? undefined : inProgress?.turnId });
  const inProgressTurnId = isTaskPath ? inProgress?.turnId : undefined;
  const { data: inProgressTaskDetail } = useAIAgentTaskShow(
    { appSlug, taskId: inProgressTurnId ?? '' },
    { enabled: Boolean(inProgressTurnId) },
  );
  const taskLog = useTaskLog({ taskId: isTaskPath ? (inProgressTaskDetail?.taskId ?? null) : null, appSlug });
  const log = isTaskPath ? taskLog : buildLog;

  const abortPath = isTaskPath ? abortAIAgentTaskPath({ appSlug, taskId: lastTurnId }) : abortBuildPath(lastTurnId);
  const abortTurnRequest = useMonolithApiMutation<unknown, unknown, void>(abortPath, 'post');

  const turnMutation = useMonolithApiMutation<
    Build | AIAgentTask,
    SimpleErrorResponse,
    {
      buildSlug: string;
      verboseMode: boolean;
      aiRunType: AIRunType & 'build-fixer-multiturn';
      userMessage: string;
    }
  >(`/apps/${appSlug}/agent_runs`, 'POST', {
    onSuccess: (response) => {
      const newTurnId = 'slug' in response.data ? response.data.slug : response.data.id;
      onUpdate({ lastTurnId: newTurnId });
      refetch();
    },
  });

  const sendMessage = () => {
    turnMutation.mutate({
      verboseMode: true,
      buildSlug,
      userMessage: message,
      aiRunType: 'build-fixer-multiturn',
    });
    setMessage('');
  };

  const handleStop = () => {
    if (!inProgress) return;

    abortTurnRequest.mutate(undefined, {
      async onSuccess() {
        if (history.length === 0) {
          onClose();
        } else {
          await refetch();
          setMessage(inProgress.userMessage);
        }
      },
    });
  };

  const isInputDisabled = Boolean(inProgress) || abortTurnRequest.isPending || turnMutation.isPending;

  return (
    <>
      <AIDrawer
        isInputDisabled={isInputDisabled}
        isLoading={turnMutation.isPending}
        isStopping={abortTurnRequest.isPending}
        message={message}
        onHide={onHide}
        onMessageChange={setMessage}
        onSend={sendMessage}
        onStop={inProgress ? handleStop : undefined}
        title="Bitrise AI"
        top={HEADER_HEIGHT}
      >
        {history?.map((msg) => (
          <AIMessage userMessage={msg.userMessage} key={msg.turnSequence}>
            <AIHistoryItem
              status={msg.status}
              durationSeconds={msg.status === 'successful' ? msg.turnDurationSeconds : undefined}
              response={msg.status === 'successful' ? msg.assistantResponse : undefined}
              onViewLog={() => setViewedTurn({ id: msg.turnId, appSlug, userMessage: msg.userMessage, isTaskPath })}
              onFeedback={(reactionType) => {
                segmentTrack('Build Whisperer Reaction Button Clicked', {
                  event_schema_version: 2.0,
                  app_slug: appSlug,
                  build_slug: msg.turnId,
                  reaction_type: reactionType,
                  is_multi_turn: true,
                });
              }}
            />
          </AIMessage>
        ))}
        {inProgress && (
          <AIMessage userMessage={inProgress.userMessage}>
            <AILoading log={log} />
          </AIMessage>
        )}
      </AIDrawer>
      <AIChatLogDialog turn={viewedTurn} onClose={() => setViewedTurn(undefined)} />
    </>
  );
};

export default MultiTurnFixer;
